// src/app/components/workshop/WorkshopHero.jsx
import Image from "next/image";
import Link from "next/link";

export default function WorkshopHero({ workshop, mentors }) {
  if (!workshop) {
    return null;
  }

 const renderSafeLink = (url, className, children) => {
  return (
    <button
      disabled
      className={`${className} opacity-60 cursor-not-allowed`}
    >
      {children}
    </button>
  );
};


  // Find mentor data for this workshop from Sanity
  const workshopMentors = mentors?.find(
    (item) =>
      item.slug === workshop.slug || item.slug === `workshops/${workshop.slug}`
  );

  // Extract mentors from Sanity data or fallback to workshop object
  const getMentorData = (mentorNumber) => {
    const nameKey = `mentor${mentorNumber}name`;
    const imageKey = `mentor${mentorNumber}image`;
    const postKey = `mentor${mentorNumber}post`;

    // Try Sanity data first
    if (workshopMentors && workshopMentors[nameKey] && workshopMentors[imageKey] && workshopMentors[postKey]) {
      return {
        name: workshopMentors[nameKey],
        image: workshopMentors[imageKey],
        post: workshopMentors[postKey]
      };
    }

    // Fallback to workshop object data
    if (workshop[nameKey] && workshop[imageKey] && workshop[postKey]) {
      return {
        name: workshop[nameKey],
        image: workshop[imageKey],
        post: workshop[postKey]
      };
    }

    return null;
  };

  // Get first two mentors
  const mentor1 = getMentorData(1);
  const mentor2 = getMentorData(2);

  // Calculate additional mentors count from Sanity data or workshop object
  const getAdditionalMentorsCount = () => {
    let count = 0;
    
    // Check Sanity data first
    if (workshopMentors) {
      for (let i = 3; i <= 12; i++) {
        if (workshopMentors[`mentor${i}name`]) {
          count++;
        }
      }
      return count;
    }

    // Fallback to workshop object
    for (let i = 3; i <= 12; i++) {
      if (workshop[`mentor${i}name`]) {
        count++;
      }
    }
    
    return count;
  };
  
  const additionalMentors = getAdditionalMentorsCount();

  return (
    <div className="w-full flex items-center justify-center mx-auto py-10 md:py-16 mt-20">
      <div className="w-[90%] md:w-[90%] lg:[80%] bg-[#FFFAF1] p-4 md:p-6 rounded-3xl border border-gray-100">
        <div className="hidden md:flex">
          <div className="flex flex-row gap-10 w-full">
            <div className="w-1/2 flex flex-col">
              <div
                className="relative w-full rounded-2xl overflow-hidden border mb-6"
                style={{ height: "350px" }}
              >
                <Image
                  src={workshop.mainImage}
                  alt={workshop.heading}
                  fill
                  className="object-cover"
                  priority
                />
              </div>
              
              <div className="flex items-center justify-between w-full">
                <div className="w-3/5 pr-4">
                  {renderSafeLink(
                    workshop.payment,
                    "bg-[#33C375] hover:bg-[#43a047] text-xl border-2 border-[#0027264D]/30 text-black font-medium px-4 py-3 rounded-lg inline-flex items-center justify-center w-full transition-colors",
                    <>
                      Register Now
                      <img src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/btnar.svg" className="ml-3"/>
                    </>
                  )}
                </div>
                
                <div className="flex flex-col items-end w-2/5">
                  {workshop.strikeprice && (
                    <span className="text-gray-500 line-through font-medium text-base">
                      {workshop.strikeprice}
                    </span>
                  )}
                  <span className="text-gray-900 text-[32px] font-semibold">
                    {workshop.price}
                  </span>
                </div>
              </div>
            </div>

            <div className="w-1/2 p-4 flex flex-col">
              <div className="flex-grow">
                <h1 className="text-3xl md:text-[40px] font-medium text-gray-900 mb-4">
                  {workshop.heading}
                </h1>
                <p className="text-gray-700 text-base mb-6">
                  {workshop.subheading}
                </p>

                <div className="flex justify-start gap-12 mb-6">
                  <div>
                    <p className="text-gray-500 text-sm">Date for Workshop</p>
                    <p className="text-gray-900 font-medium">Coming Soon</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Time for Workshop</p>
                    <p className="text-gray-900 font-medium">{workshop.time}</p>
                  </div>
                  {workshop.duration && (
                    <div>
                      <p className="text-gray-500 text-sm">Duration</p>
                      <p className="text-gray-900 font-medium">
                        {workshop.duration}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-auto">
                <p className="text-gray-700 mb-4">Mentors</p>
                <div className="flex flex-wrap items-center gap-6">
                  {mentor1 && (
                    <div className="flex items-center">
                      <div className="relative w-12 h-12 rounded-md overflow-hidden border border-gray-200">
                        <Image
                          src={mentor1.image}
                          alt={mentor1.name}
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">{mentor1.name}</p>
                        <p className="text-gray-600 text-sm">{mentor1.post}</p>
                      </div>
                    </div>
                  )}
                  
                  {mentor2 && (
                    <div className="flex items-center">
                      <div className="relative w-12 h-12 rounded-md overflow-hidden border border-gray-200">
                        <Image
                          src={mentor2.image}
                          alt={mentor2.name}
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">{mentor2.name}</p>
                        <p className="text-gray-600 text-sm">{mentor2.post}</p>
                      </div>
                    </div>
                  )}
                  
                  {!mentor1 && workshop.mentorImage && (
                    <div className="flex items-center">
                      <div className="relative w-12 h-12 rounded-md overflow-hidden border border-gray-200">
                        <Image
                          src={workshop.mentorImage}
                          alt={workshop.name}
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">{workshop.name}</p>
                        <p className="text-gray-600 text-sm">{workshop.designation}</p>
                      </div>
                    </div>
                  )}
                  
                  {additionalMentors > 0 && (
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium text-lg">
                        +{additionalMentors} more
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:hidden">
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-5 border"
            style={{ height: "200px" }}
          >
            <Image
              src={workshop.mainImage}
              alt={workshop.heading}
              fill
              className=""
              priority
            />
          </div>

          <div className="mb-4">
            <p className="text-gray-700 mb-2">Mentors</p>
            <div className="flex items-center space-x-3">
              {mentor1 && (
                <div className="flex items-center">
                  <div className="relative w-10 h-10 rounded-md overflow-hidden border border-gray-200">
                    <Image
                      src={mentor1.image}
                      alt={mentor1.name}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <div className="ml-2">
                    <p className="font-medium text-gray-900 text-sm">{mentor1.name}</p>
                    <p className="text-gray-600 text-xs">{mentor1.post}</p>
                  </div>
                </div>
              )}
              
              {(mentor2 || additionalMentors > 0) && (
                <span className="text-gray-600 text-sm font-medium">
                  +{mentor2 ? (additionalMentors > 0 ? additionalMentors + 1 : 1) : additionalMentors} more
                </span>
              )}
              
              {!mentor1 && workshop.mentorImage && (
                <div className="flex items-center">
                  <div className="relative w-10 h-10 rounded-md overflow-hidden border border-gray-200">
                    <Image
                      src={workshop.mentorImage}
                      alt={workshop.name}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <div className="ml-2">
                    <p className="font-medium text-gray-900 text-sm">{workshop.name}</p>
                    <p className="text-gray-600 text-xs">{workshop.designation}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {workshop.heading}
          </h1>
          <p className="text-gray-700 text-sm mb-4">{workshop.subheading}</p>

          <div className="flex justify-start gap-5 mb-5">
            <div>
              <p className="text-gray-500 text-xs">Date for Workshop</p>
              <p className="text-gray-900 font-medium text-sm">{workshop.date}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Time for Workshop</p>
              <p className="text-gray-900 font-medium text-sm">{workshop.time}</p>
            </div>
          </div>

          <div className="flex mb-4 items-center">
            <span className="text-gray-900 text-xl font-semibold mr-2">
              Now at {workshop.price}
            </span>
            {workshop.strikeprice && (
              <span className="text-gray-500 line-through text-base">
                {workshop.strikeprice}
              </span>
            )}
          </div>

          {renderSafeLink(
            workshop.payment,
            "bg-[#33C375] border-2 border-[#0027264D]/30 text-base hover:bg-[#43a047] text-black font-medium py-3 rounded-lg flex items-center justify-center w-full transition-colors",
            <>
              Register Now
              <img src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/btnar.svg" className="ml-2"/>
            </>
          )}
        </div>
      </div>
    </div>
  );
}